import { cn } from './utils/styles';
import type { SuperBarViewState } from './context/use-chat-context';
import { iconSlotSizeClasses } from './constants/ai-icons';
import { useIsIOSWebView } from './hooks/useIsIOSWebView';
import { useSuperbarTheme } from './hooks/useSuperbarTheme';

type LoadingAnimationState = 'stable' | 'loading';

interface LoadingAnimationProps {
  state: LoadingAnimationState;
  superBarViewState: SuperBarViewState;
}

export const LoadingAnimation = ({
  state,
  superBarViewState,
}: LoadingAnimationProps) => {
  const theme = useSuperbarTheme();
  const isLoading = state === 'loading';
  const isIOSWebView = useIsIOSWebView();

  const sizeClasses = iconSlotSizeClasses(superBarViewState);

  const opacityClass = !isLoading ? theme.components.loader.stableOpacity : '';

  // If running in iOS WebView, just display the poster images with animation
  if (isIOSWebView) {
    return (
      <div
        className={cn(
          'caddie-loading-anim caddietw-items-center caddietw-justify-center caddietw-flex caddietw-relative',
          sizeClasses,
          opacityClass
        )}
      >
        <img
          src={
            isLoading
              ? 'https://cdn.trycaddie.com/fast-thinking.png'
              : 'https://cdn.trycaddie.com/slow-stable.png'
          }
          alt="Loading animation"
          className={cn(sizeClasses)}
          style={
            isLoading
              ? {
                  animation: 'scalePulse 1.5s ease-in-out infinite',
                }
              : undefined
          }
        />
        {isLoading && (
          <style>{`
            @keyframes scalePulse {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.1); }
            }
          `}</style>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'caddie-loading-anim caddietw-items-center caddietw-justify-center caddietw-flex caddietw-relative',
        sizeClasses,
        opacityClass,
        theme.components.loader.className
      )}
    >
      <video
        autoPlay
        loop
        muted
        playsInline
        webkit-playsinline=""
        disablePictureInPicture
        controls={false}
        controlsList="nofullscreen noremoteplayback"
        className={cn(
          'caddie-loading-anim-loading caddietw-absolute caddietw-left-0 caddietw-top-0 caddietw-z-20 bg-loading-video',
          isLoading ? 'caddietw-opacity-100' : 'caddietw-opacity-0',
          sizeClasses,
          theme.components.loader.className
        )}
        key="loading-fast-video"
        preload="auto"
        poster="https://cdn.trycaddie.com/fast-thinking.png"
      >
        <source
          src="https://cdn.trycaddie.com/fast-thinking.webm"
          type="video/webm"
        />
        <source
          src="https://cdn.trycaddie.com/fast-thinking.mp4"
          type="video/mp4"
        />
      </video>

      <video
        autoPlay
        loop
        muted
        playsInline
        webkit-playsinline=""
        disablePictureInPicture
        controls={false}
        controlsList="nofullscreen noremoteplayback"
        className={cn(
          'caddie-loading-anim-not-loading caddietw-absolute caddietw-left-0 caddietw-top-0 caddietw-z-10 bg-loading-video',
          isLoading ? 'caddietw-opacity-0' : 'caddietw-opacity-100',
          sizeClasses,
          theme.components.loader.className
        )}
        key="not-loading-slow-video"
        preload="auto"
        poster="https://cdn.trycaddie.com/slow-stable.png"
      >
        <source
          src="https://cdn.trycaddie.com/slow-stable.webm"
          type="video/webm"
        />
        <source
          src="https://cdn.trycaddie.com/slow-stable.mp4"
          type="video/mp4"
        />
      </video>
    </div>
  );
};
